import React, { Suspense, useEffect } from "react";
import HeaderComponent from "./components/Header.tsx";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { initializeBFCache } from "./utils/bfcache.ts";

// Lazy loading dos componentes para melhorar performance
const Home = React.lazy(() => import("./pages/Home.tsx"));
const Sobre = React.lazy(() => import("./pages/Sobre.tsx"));
const WatchfolioWeb = React.lazy(() => import("./pages/WatchfolioWeb.tsx"));
const AccountDelete = React.lazy(() => import("./pages/accountDelete.tsx"));
const AccountDeleteEn = React.lazy(() => import("./pages/accountDelete-en.tsx"));
const DownloadApp = React.lazy(() => import("./pages/DownloadApp.tsx"));
const Login = React.lazy(() => import("./pages/Login.tsx"));
const Controle = React.lazy(() => import("./pages/Controle.tsx"));
const Dashboard = React.lazy(() => import("./pages/Dashboard.tsx"));
const Blog = React.lazy(() => import("./pages/Blog.tsx"));
const BlogPost = React.lazy(() => import("./pages/BlogPost.tsx"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy.tsx"));
const MovieDetails = React.lazy(() => import("./pages/MovieDetails.tsx"));
const UserProfile = React.lazy(() => import("./pages/UserProfile.tsx"));
const ActorDetails = React.lazy(() => import("./pages/ActorDetails.tsx"));
const PostDetail = React.lazy(() => import("./pages/PostDetail.tsx"));
const MiniMystics = React.lazy(() => import("./pages/MiniMystics.tsx"));
// Rotas disponíveis apenas com: npm run dev:secret
// Em produção (npm run build) o webpack elimina esses imports do bundle.
const SECRET_ENABLED = process.env.REACT_APP_SECRET_ENABLED === 'true';
const SecretProject = SECRET_ENABLED ? React.lazy(() => import("./pages/SecretProject.tsx")) : null;
const SecretProjectDetail = SECRET_ENABLED ? React.lazy(() => import("./pages/SecretProjectDetail.tsx")) : null;
const SecretProjectPerformer = SECRET_ENABLED ? React.lazy(() => import("./pages/SecretProjectPerformer.tsx")) : null;
const Nos2 = React.lazy(() => import("./pages/Nos2.tsx"));
const Readfolio = React.lazy(() => import("./pages/Readfolio.tsx"));
const GeoLandingPage = React.lazy(() => import("./pages/GeoLandingPage.tsx"));
import PrivateRoute from "./components/PrivateRoute.tsx";

import "./App.css";
import "./Style.css";
import Footer from "./components/Footer.tsx";

// Componente de loading
const LoadingComponent = () => (
  <div style={{ background: '#09050d', minHeight: '100vh', padding: '0' }}>
    <div style={{
      width: '100%', height: '56vw', maxHeight: '520px',
      background: 'linear-gradient(90deg,#1a1230 25%,#2a1e42 50%,#1a1230 75%)',
      backgroundSize: '1200px 100%',
      animation: 'sk-shimmer 1.6s ease infinite',
      marginBottom: '32px',
    }} />
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ padding: '0 4%', marginBottom: '36px' }}>
        <div style={{
          width: '180px', height: '22px', borderRadius: '4px', marginBottom: '12px',
          background: 'linear-gradient(90deg,#1a1230 25%,#2a1e42 50%,#1a1230 75%)',
          backgroundSize: '1200px 100%',
          animation: 'sk-shimmer 1.6s ease infinite',
        }} />
        <div style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}>
          {Array.from({ length: 8 }).map((_, j) => (
            <div key={j} style={{
              flexShrink: 0, width: '130px', height: '195px', borderRadius: '6px',
              background: 'linear-gradient(90deg,#1a1230 25%,#2a1e42 50%,#1a1230 75%)',
              backgroundSize: '1200px 100%',
              animation: 'sk-shimmer 1.6s ease infinite',
            }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

function App() {
  useEffect(() => {
    // Inicializa otimizações de BFCache
    initializeBFCache();
  }, []);

  return (
    <Router>
      <div>
        <HeaderComponent />
        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            <Route path="" element={<Home />} />
            <Route path="/sobre" element={<Sobre />} />
            <Route path="/watchfolioWeb" element={<WatchfolioWeb />} />
            <Route path="/movie/:id" element={<MovieDetails />} />
            <Route path="/actor/:id" element={<ActorDetails />} />
            <Route path="/profile/:userId" element={<UserProfile />} />
            <Route path="/post/:postId" element={<PostDetail />} />
            <Route path="/deleteAccount" element={<AccountDelete />} />
            <Route path="/deleteAccount-en" element={<AccountDeleteEn />} />
            <Route path="/downloadApp" element={<DownloadApp />} />
            <Route path="/app" element={<DownloadApp />} />
            <Route path="/minimystics" element={<MiniMystics />} />
            <Route path="/nos2" element={<Nos2 />} />
            <Route path="/readfolio" element={<Readfolio />} />
            <Route path="/login" element={<Login />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            {/* secretProject — acessível apenas com: npm run dev:secret */}
            {SECRET_ENABLED && SecretProject && SecretProjectDetail && SecretProjectPerformer && <>
              <Route path="/secretProject" element={<SecretProject />} />
              <Route path="/searchsecretProject" element={<SecretProject />} />
              <Route path="/secretProject/detail/:type/:id" element={<SecretProjectDetail />} />
              <Route path="/secretProject/performer/:id" element={<SecretProjectPerformer />} />
            </>}
            {/* GEO Landing Pages */}
            <Route path="/geo/:slug" element={<GeoLandingPage />} />
            <Route path="/alternativa-ao-letterboxd" element={<GeoLandingPage />} />
            <Route path="/melhor-app-para-descobrir-filmes" element={<GeoLandingPage />} />
            <Route path="/rede-social-de-filmes" element={<GeoLandingPage />} />
            <Route path="/ia-para-recomendar-filmes" element={<GeoLandingPage />} />
            <Route path="/onde-assistir-filmes-gratis-legalmente" element={<GeoLandingPage />} />
            <Route path="/onde-assistir-series-gratis-legalmente" element={<GeoLandingPage />} />
            <Route path="/watchfolio-vs-letterboxd" element={<GeoLandingPage />} />
            <Route path="/apps-para-organizar-filmes-assistidos" element={<GeoLandingPage />} />
            <Route path="/app-para-recomendacao-de-filmes" element={<GeoLandingPage />} />
            <Route path="/melhores-apps-para-cinefilos" element={<GeoLandingPage />} />
            <Route path="/watchfolio-press-kit" element={<GeoLandingPage />} />
            <Route path="/privacidade" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route 
              path="/controle" 
              element={
                <PrivateRoute requireAdmin={true}>
                  <Controle />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute requireAdmin={true}>
                  <Dashboard />
                </PrivateRoute>
              } 
            />
          </Routes>
        </Suspense>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
